import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Tooltip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useLanguage } from '../context/LanguageContext';
import { useTeluguYear } from '../context/TeluguYearContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import PattaCertificate from '../components/PattaCertificate';
import ImageAdjustmentDialog from '../components/ImageAdjustmentDialog';
import ViewAllPattasDialog from '../components/ViewAllPattasDialog';
import { LocalizedInput } from '../utils/grandhamTreeUtils';

const PattaGeneration = () => {
  const { lang } = useLanguage();
  const { teluguYearTelugu, yearId } = useTeluguYear();
  const { user } = useAuth();

  // Only Admin can access this page
  const isAuthorized = user && user.role === 'Admin';

  const [eligibleVatus, setEligibleVatus] = useState([]);
  const [selectedVatuIds, setSelectedVatuIds] = useState([]);
  const [generatedCertificates, setGeneratedCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewCertificate, setPreviewCertificate] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [editingCertificateId, setEditingCertificateId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [openPhotoAdjustmentDialog, setOpenPhotoAdjustmentDialog] = useState(false);
  const [savingPhotoConfig, setSavingPhotoConfig] = useState(false);
  const [openViewAllPattasDialog, setOpenViewAllPattasDialog] = useState(false);
  const [issueConfirmDialog, setIssueConfirmDialog] = useState({ open: false, certificateId: null });
  const [issuingPatta, setIssuingPatta] = useState(false);
  const printRef = useRef();

  const labels = {
    telugu: {
      title: 'పట్టా జనరేషన్',
      selectVatus: 'విద్యార్థులను ఎంచుకోండి',
      generatePatta: 'పట్టా జనరేట్ చేయండి',
      noEligible: 'పట్టా అర్హత కలిగిన విద్యార్థులు లేరు',
      generating: 'జనరేట్ చేస్తోంది...',
      generated: 'సర్టిఫికెట్లు విజయవంతంగా జనరేట్ అయ్యాయి',
      preview: 'ప్రివ్యూ',
      download: 'డౌన్‌లోడ్',
      print: 'ప్రింట్',
      pathasala: 'పాఠశాల',
      kramantham: 'క్రమాంతం',
      certificateId: 'సర్టిఫికెట్ నంబర్',
      issueDate: 'జారీ తేదీ',
      generatedCertificates: 'జనరేట్ చేసిన సర్టిఫికెట్లు',
      edit: 'సవరించు',
      save: 'సేవ్ చేయండి',
      cancel: 'రద్దు చేయండి',
      teluguYear: 'సంవత్సరం',
      prantham: 'ప్రాంతం',
      gothram: 'గోత్రం',
      vatuName: 'వటువు పేరు',
      guruvuName: 'గురువు పేరు',
      shreni: 'శ్రేణి',
      editCertificateDetails: 'సర్టిఫికెట్ వివరాలను సవరించండి',
      notSanskrit: 'సంస్కృతం కాదు - అనువాదం చేస్తుంది',
      isSanskrit: 'సంస్కృతం కొద్దీ',
      issue: 'జారీ చేయండి',
      issueConfirmTitle: 'పట్టా జారీ చేయడం',
      issueConfirmMessage: 'దయచేసి నిర్ధారించండి - ఈ పట్టాను జారీ చేయాలనుకుంటున్నారా?',
      issueConfirm: 'అవును, జారీ చేయండి',
      issueSuccess: 'పట్టా విజయవంతంగా జారీ చేయబడింది'
    },
    sanskrit: {
      title: 'पट्टा निर्माणम्',
      selectVatus: 'वटून् वरणयतु',
      generatePatta: 'पट्टां निर्मातु',
      noEligible: 'पट्टायोग्याः वटवः न सन्ति',
      generating: 'निर्मीयते...',
      generated: 'प्रमाणपत्राणि सफलतया निर्मितानि',
      preview: 'पूर्वावलोकनम्',
      download: 'अवतारणम्',
      print: 'मुद्रणम्',
      pathasala: 'पाठशाला',
      kramantham: 'क्रमान्तम्',
      certificateId: 'प्रमाणपत्र संख्या',
      issueDate: 'निर्गम तिथिः',
      generatedCertificates: 'निर्मित प्रमाणपत्राणि',
      edit: 'संशोधयतु',
      save: 'रक्षतु',
      cancel: 'निरस्यतु',
      teluguYear: 'संवत्सरः',
      prantham: 'प्रान्तम्',
      gothram: 'गोत्रम्',
      vatuName: 'वटु नाम',
      guruvuName: 'गुरु नाम',
      shreni: 'श्रेणी',
      editCertificateDetails: 'प्रमाणपत्र विवरणानि संशोधयतु',
      notSanskrit: 'संस्कृतं नहीं - अनुवाद करते हुए',
      isSanskrit: 'संस्कृत अभिज्ञः',
      issue: 'निर्गमयतु',
      issueConfirmTitle: 'पट्टा निर्गमणम्',
      issueConfirmMessage: 'कृपया सत्यापयतु - किम् एतां पट्टां निर्गमयितुम् इच्छति?',
      issueConfirm: 'आम्, निर्गमयतु',
      issueSuccess: 'पट्टा सफलतया निर्गमिता'
    }
  };

  const currentLabels = labels[lang];

  useEffect(() => {
    if (yearId) {
      fetchEligibleVatus();
    }
  }, [yearId]);

  const fetchEligibleVatus = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get(`/api/certificate/patta-eligible?year_id=${encodeURIComponent(yearId)}`);
      const data = await res.json();
      if (data.success) {
        setEligibleVatus(data.data || []);
      } else {
        setError(data.error || 'Failed to fetch eligible vatus');
      }
    } catch (err) {
      console.error('Error fetching eligible vatus:', err);
      setError('Failed to fetch eligible vatus');
    } finally {
      setLoading(false);
    }
  };

  const handleVatuSelect = (event) => {
    const value = event.target.value;
    setSelectedVatuIds(typeof value === 'string' ? value.split(',') : value);
  };

  const validatePhone = (value) => {
    const localDigits = String(value || '').replace(/\D/g, '');
    if (!value) return '';
    if (localDigits.length !== 10) return 'Enter a 10-digit local phone number';
    return '';
  };

  const validateEmail = (value) => {
    if (!value) return '';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) return 'Invalid email';
    return '';
  };

  const handleGeneratePatta = async () => {
    if (selectedVatuIds.length === 0) {
      setError('Please select at least one vatu');
      return;
    }

    try {
      setGenerating(true);
      setError('');
      setSuccess('');

      const res = await api.post('/api/certificate/generate', {
        vatu_ids: selectedVatuIds,
        year_id: yearId
      });

      const data = await res.json();

      if (data.success) {
        setGeneratedCertificates(data.data || []);
        setSuccess(currentLabels.generated);
        setSelectedVatuIds([]);
        fetchEligibleVatus();
      } else {
        setError(data.error || 'Failed to generate certificates');
      }
    } catch (err) {
      console.error('Error generating certificates:', err);
      setError('Failed to generate certificates');
    } finally {
      setGenerating(false);
    }
  };

  const handlePreview = async (certificate) => {
    try {
      // Fetch certificate with all signature URLs
      const certRes = await api.get(`/api/certificate/with-signatures/${certificate.certificate_id}`);
      const certData = await certRes.json();
      
      if (certData.success && certData.data) {
        // Use the enriched certificate data
        setPreviewCertificate(certData.data);
        setOpenPreview(true);
        return;
      }
    } catch (err) {
      console.error('Error fetching certificate with signatures:', err);
    }
    
    // Fallback: fetch vatu details for photo URL
    try {
      const res = await api.get(`/api/vatu/${certificate.vatu_id}`);
      const vatuData = await res.json();
      
      if (vatuData.success && vatuData.data) {
        certificate.vatu_photo_url = vatuData.data.photo_download_link || null;
      }
    } catch (err) {
      console.error('Error fetching vatu details:', err);
    }
    
    setPreviewCertificate(certificate);
    setOpenPreview(true);
  };

  // Handle preview from ViewAllPattasDialog - data already fetched with signatures
  const handlePreviewFromViewAll = (certificateData) => {
    if (certificateData) {
      setPreviewCertificate(certificateData);
      setOpenPreview(true);
    }
  };

  const handleDownloadPDF = async (certificateId) => {
    try {
      // If preview is not open, open it first
      if (!openPreview) {
        const cert = generatedCertificates.find(c => c.certificate_id === certificateId);
        if (!cert) {
          throw new Error('Certificate not found');
        }
        
        // Fetch certificate with signatures
        try {
          const certRes = await api.get(`/api/certificate/with-signatures/${certificateId}`);
          const certData = await certRes.json();
          if (certData.success && certData.data) {
            setPreviewCertificate(certData.data);
            setOpenPreview(true);
            // Wait for preview to render
            setTimeout(() => generatePDFFromPreview(certificateId), 500);
            return;
          }
        } catch (err) {
          console.error('Error fetching certificate:', err);
        }
        
        setPreviewCertificate(cert);
        setOpenPreview(true);
        // Wait for preview to render
        setTimeout(() => generatePDFFromPreview(certificateId), 500);
        return;
      }
      
      // If preview is already open, generate PDF immediately
      generatePDFFromPreview(certificateId);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError(err.message || 'Failed to download PDF');
    }
  };

  const generatePDFFromPreview = (certificateId) => {
    try {
      if (!printRef.current) {
        throw new Error('Preview content not available');
      }
      
      // Load html2canvas library to capture as image
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
      script.onload = () => {
        const element = printRef.current.querySelector('.cert-page');
        if (!element) {
          setError('Certificate page element not found');
          return;
        }
        
        window.html2canvas(element, {
          scale: 2,
          allowTaint: true,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false
        }).then(canvas => {
          // Convert canvas to blob and download as image
          canvas.toBlob((blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `certificate-${certificateId}.png`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }, 'image/png', 0.95);
        }).catch(err => {
          console.error('Error generating image:', err);
          setError('Failed to generate certificate image');
        });
      };
      script.onerror = () => {
        throw new Error('Failed to load canvas library');
      };
      document.head.appendChild(script);
    } catch (err) {
      console.error('Error downloading certificate:', err);
      setError(err.message || 'Failed to download certificate');
    }
  };

  const handlePrint = () => {
    if (printRef.current) {
      const printContent = printRef.current.innerHTML;
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Certificate</title>
          <link href="https://fonts.googleapis.com/css2?family=Noto+Serif+Devanagari:wght@400;600;700&family=Noto+Sans+Telugu:wght@400;600;700&display=swap" rel="stylesheet">
          <style>
            @page { size: A4; margin: 0; }
            body { margin: 0; padding: 0; }
          </style>
        </head>
        <body>${printContent}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      };
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  };

  // Handle opening edit dialog
  const handleOpenEditDialog = (certificate) => {
    setEditingCertificateId(certificate.certificate_id);
    setEditFormData({
      telugu_year: certificate.telugu_year || '',
      prantham: certificate.prantham || '',
      gothram: certificate.gothram || '',
      vatu_name: certificate.vatu_name || '',
      guruvu_gari_name: certificate.guruvu_name || '',
      shreni: certificate.shreni || '',
      phone: certificate.phone || '',
      email: certificate.email || ''
    });
    setOpenEditDialog(true);
  };

  // Handle saving edited certificate details
  const handleSaveEditDetails = async () => {
    try {
      setSavingEdit(true);
      setError('');

      const res = await api.put(`/api/certificate/${editingCertificateId}/details`, {
        telugu_year: editFormData.telugu_year,
        prantham: editFormData.prantham,
        gothram: editFormData.gothram,
        vatu_name: editFormData.vatu_name,
        guruvu_gari_name: editFormData.guruvu_gari_name,
        shreni: editFormData.shreni,
        phone: editFormData.phone,
        email: editFormData.email
      });

      const data = await res.json();

      if (data.success) {
        // Map guruvu_gari_name to guruvu_name for certificate model
        const updateData = {
          ...editFormData,
          guruvu_name: editFormData.guruvu_gari_name
        };
        
        // Update the certificate in the list
        setGeneratedCertificates(generatedCertificates.map(cert => 
          cert.certificate_id === editingCertificateId 
            ? { ...cert, ...updateData }
            : cert
        ));
        
        // Also update preview if it's open
        if (previewCertificate && previewCertificate.certificate_id === editingCertificateId) {
          setPreviewCertificate({ ...previewCertificate, ...updateData });
        }

        setSuccess(lang === 'telugu' ? 'సర్టిఫికెట్ వివరాలు విజయవంతంగా నవీకరించబడ్డాయి' : 'प्रमाणपत्र विवरणानि सफलतया अद्यतनानि');
        setOpenEditDialog(false);
        setEditingCertificateId(null);
      } else {
        setError(data.error || 'Failed to update certificate details');
      }
    } catch (err) {
      console.error('Error saving certificate details:', err);
      setError('Failed to save certificate details');
    } finally {
      setSavingEdit(false);
    }
  };

  // Handle field blur - translate to Sanskrit if needed
  const handleFieldBlur = async (fieldName, value) => {
    if (!value || typeof value !== 'string') return;

    try {
      // Call translation API
      const res = await api.post('/api/certificate/translate', { text: value });
      const data = await res.json();

      if (data.success) {
        // Update field with translated value
        setEditFormData(prev => ({
          ...prev,
          [fieldName]: data.translated
        }));
      }
    } catch (err) {
      console.error('Error translating field:', err);
      // Keep original value on error
    }
  };

  // Transform certificate object to PattaCertificate data format
  // Passes all data directly as the component now handles the formatting
  const transformCertificateData = (certificate) => {
    if (!certificate) return null;
    console.log('Transforming certificate data:', certificate);

    return {
      // Direct certificate data - component will use these
      ...certificate,
      // QR code MinIO path will be used to download from endpoint
      qr_code_minio_path: certificate.qr_code_minio_path || null,
      // Use vatu photo if available
      rightImage: certificate.vatu_photo_url || null,
      // Backward compatibility fields
      vatuName: certificate.vatu_name,
      guruName: certificate.guruvu_name,
      pathasalaName: certificate.pathasala_name,
      teluguYear: certificate.telugu_year,
      verificationId: certificate.verification_id
    };
  };

  const handleSavePhotoConfig = async (config) => {
    try {
      setSavingPhotoConfig(true);
      const response = await api.put(
        `/api/certificate/${previewCertificate?.certificate_id}/photo-config`,
        config
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSuccess('Photo configuration saved successfully');
          // Update the preview certificate with new config
          setPreviewCertificate(prev => ({
            ...prev,
            vatu_photo_object_config: config
          }));
          setOpenPhotoAdjustmentDialog(false);
          setTimeout(() => setSuccess(''), 3000);
        }
      } else {
        throw new Error('Failed to save photo configuration');
      }
    } catch (err) {
      console.error('Error saving photo config:', err);
      setError(err.message || 'Failed to save photo configuration');
      setTimeout(() => setError(''), 3000);
    } finally {
      setSavingPhotoConfig(false);
    }
  };

  // Handle issue patta confirmation
  const handleIssuePattaClick = (certificateId) => {
    setIssueConfirmDialog({ open: true, certificateId });
  };

  // Confirm and issue patta
  const handleConfirmIssuePatta = async () => {
    const { certificateId } = issueConfirmDialog;
    
    try {
      setIssuingPatta(true);
      setError('');

      const response = await api.post(`/api/certificate/${certificateId}/issue`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSuccess(currentLabels.issueSuccess);
          
          // Update the certificate in the list - mark as issued
          setGeneratedCertificates(generatedCertificates.map(cert =>
            cert.certificate_id === certificateId
              ? { ...cert, status: 'issued' }
              : cert
          ));

          setIssueConfirmDialog({ open: false, certificateId: null });
          setTimeout(() => setSuccess(''), 3000);
        }
      } else {
        throw new Error('Failed to issue patta');
      }
    } catch (err) {
      console.error('Error issuing patta:', err);
      setError(err.message || 'Failed to issue patta');
    } finally {
      setIssuingPatta(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!isAuthorized) {
    return (
      <Container maxWidth="lg">
        <Paper elevation={3} sx={{ p: 4, mt: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error">
            {lang === 'telugu' ? 'అనుమతి లేదు' : 'अनुमति नहीं'}
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {lang === 'telugu' 
              ? 'ఈ పేజీని చూడటానికి మీకు అధికారం లేదు' 
              : 'आपको यह पृष्ठ देखने की अनुमति नहीं है'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, color: "#7f4b2d" }}>
            {currentLabels.title}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<LibraryBooksIcon />}
            onClick={() => setOpenViewAllPattasDialog(true)}
            sx={{
              borderColor: "#7f4b2d",
              color: "#7f4b2d",
              "&:hover": {
                backgroundColor: "#f0f4ff",
                borderColor: "#7f4b2d",
              },
            }}
          >
            View All Pattas
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        <Box sx={{ mb: 4 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{currentLabels.selectVatus}</InputLabel>
            <Select
              multiple
              value={selectedVatuIds}
              onChange={handleVatuSelect}
              label={currentLabels.selectVatus}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((vatuId) => {
                    const vatu = eligibleVatus.find(
                      (v) => v.vatu_id === vatuId,
                    );
                    return (
                      <Chip
                        key={vatuId}
                        label={`${vatu?.vatu_name || vatuId} (${vatu?.kramantham_display || ""})`}
                        size="small"
                        onDelete={() => {
                          setSelectedVatuIds(
                            selectedVatuIds.filter((id) => id !== vatuId),
                          );
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {eligibleVatus.length === 0 ? (
                <MenuItem disabled>{currentLabels.noEligible}</MenuItem>
              ) : (
                eligibleVatus.map((vatu) => (
                  <MenuItem key={vatu.vatu_id} value={vatu.vatu_id}>
                    <Checkbox
                      checked={selectedVatuIds.includes(vatu.vatu_id)}
                    />
                    <ListItemText
                      primary={`${vatu.vatu_name} शर्म्मा`}
                      secondary={`${vatu.pathasala_name} | ${vatu.kramantham_display || vatu.vatuDetails?.krishnaYajurvedaKramantham || ""} | ${vatu.shreni || ""}`}
                    />
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleGeneratePatta}
            disabled={selectedVatuIds.length === 0 || generating}
            sx={{
              backgroundColor: "#2e7d32",
              "&:hover": { backgroundColor: "#27632a" },
              minWidth: 200,
            }}
          >
            {generating ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} sx={{ color: "#fff" }} />
                <span>{currentLabels.generating}</span>
              </Box>
            ) : (
              currentLabels.generatePatta
            )}
          </Button>
        </Box>

        {generatedCertificates.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, color: "#7f4b2d", fontWeight: 600 }}
            >
              {currentLabels.generatedCertificates}
            </Typography>
            <Grid container spacing={2}>
              {generatedCertificates.map((cert) => (
                <Grid item xs={12} sm={6} md={4} key={cert.certificate_id}>
                  <Card
                    sx={{
                      border: "2px solid #2e7d32",
                      backgroundColor: "#f1f8e9",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: "#2e7d32" }}
                      >
                        {cert.vatu_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {currentLabels.certificateId}: {cert.certificate_id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {currentLabels.pathasala}: {cert.pathasala_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {currentLabels.issueDate}: {formatDate(cert.issue_date)}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                        <Tooltip title={currentLabels.issue}>
                          <IconButton
                            size="small"
                            sx={{ color: "#2e7d32" }}
                            onClick={() =>
                              handleIssuePattaClick(cert.certificate_id)
                            }
                            disabled={cert.status === "issued" || issuingPatta}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={currentLabels.preview}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handlePreview(cert)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={currentLabels.edit}>
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleOpenEditDialog(cert)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={currentLabels.download}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() =>
                              handleDownloadPDF(cert.certificate_id)
                            }
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Paper>

      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="h6" sx={{ m: 0 }}>
            {currentLabels.preview}
          </Typography>
          <Box>
            <Tooltip title="Adjust Photo">
              <IconButton
                onClick={() => setOpenPhotoAdjustmentDialog(true)}
                sx={{ mr: 1 }}
              >
                <TuneIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={handlePrint} sx={{ mr: 1 }}>
              <PrintIcon />
            </IconButton>
            <IconButton
              onClick={() =>
                handleDownloadPDF(previewCertificate?.certificate_id)
              }
            >
              <DownloadIcon />
            </IconButton>
            <IconButton onClick={() => setOpenPreview(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            backgroundColor: "#f5f5f5",
            p: 0,
            m: 0,
            minHeight: "auto",
            overflow: "visible",
          }}
        >
          <div
            ref={printRef}
            style={{
              margin: 0,
              padding: 0,
              width: "fit-content",
              height: "fit-content",
            }}
          >
            <PattaCertificate
              data={transformCertificateData(previewCertificate)}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Certificate Details Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="h6" sx={{ m: 0 }}>
            {currentLabels.editCertificateDetails}
          </Typography>
          <IconButton
            onClick={() => setOpenEditDialog(false)}
            disabled={savingEdit}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2 }}>
            <LocalizedInput
              value={editFormData.telugu_year}
              setValue={(value) =>
                setEditFormData({ ...editFormData, telugu_year: value })
              }
              labels={{ telugu: "సంవత్సరం", sanskrit: "संवत्सरः" }}
              placeholders="eg. श्री विश्वावसु"
              lang="sanskrit"
              fullWidth
            />
            <LocalizedInput
              value={editFormData.prantham}
              setValue={(value) =>
                setEditFormData({ ...editFormData, prantham: value })
              }
              labels={{ telugu: "ప్రాంతం", sanskrit: "अभिजनः" }}
              placeholders="eg. भाग्यनगरम्"
              lang="sanskrit"
              fullWidth
            />
            <LocalizedInput
              value={editFormData.gothram}
              setValue={(value) =>
                setEditFormData({ ...editFormData, gothram: value })
              }
              labels={{ telugu: "గోత్రం", sanskrit: "गोत्रः" }}
              placeholders="eg. भरद्वाज"
              lang="sanskrit"
              fullWidth
            />
            <LocalizedInput
              value={editFormData.vatu_name}
              setValue={(value) =>
                setEditFormData({ ...editFormData, vatu_name: value })
              }
              labels={{ telugu: "వటువు పేరు", sanskrit: "वटु नाम" }}
              placeholders="eg. रामा"
              lang="sanskrit"
              fullWidth
            />
            <LocalizedInput
              value={editFormData.guruvu_gari_name}
              setValue={(value) =>
                setEditFormData({ ...editFormData, guruvu_gari_name: value })
              }
              labels={{ telugu: "గురువు పేరు", sanskrit: "गुरु नाम" }}
              placeholders="eg. शर्मा"
              lang="sanskrit"
              fullWidth
            />
            <LocalizedInput
              value={editFormData.shreni}
              setValue={(value) =>
                setEditFormData({ ...editFormData, shreni: value })
              }
              labels={{ telugu: "శ్రేణి", sanskrit: "श्रेणी" }}
              placeholders="eg. प्रथम श्रेणी"
              lang="sanskrit"
              fullWidth
            />
            <TextField
              label={lang === "telugu" ? "ఫోన్ (లోకల్)" : "स्थानीय फोन"}
              value={editFormData.phone}
              onChange={(e) =>
                setEditFormData({ ...editFormData, phone: e.target.value })
              }
              placeholder="9876543210"
              inputProps={{ maxLength: 10 }}
              error={!!validatePhone(editFormData.phone)}
              helperText={validatePhone(editFormData.phone  )}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">+91</InputAdornment>
                ),
              }}
            />
            <TextField
              label={lang === "telugu" ? "ఇ-మెయిల్" : "ईमेल"}
              value={editFormData.email}
              onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
              placeholder={
                lang === "telugu" ? "ఇ-మెయిల్ నమోదుచేయండి" : "ईमेलम् लिखतु"
              }
              error={!!validateEmail(editFormData.email)}
              helperText={validateEmail(editFormData.email)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Button
            onClick={() => setOpenEditDialog(false)}
            disabled={savingEdit}
          >
            {currentLabels.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEditDetails}
            disabled={savingEdit}
            startIcon={
              savingEdit ? <CircularProgress size={20} /> : <SaveIcon />
            }
          >
            {currentLabels.save}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo Adjustment Dialog */}
      <ImageAdjustmentDialog
        open={openPhotoAdjustmentDialog}
        onClose={() => setOpenPhotoAdjustmentDialog(false)}
        imageUrl={
          previewCertificate?.rightImage || previewCertificate?.vatu_photo_url
        }
        currentConfig={previewCertificate?.vatu_photo_object_config}
        onSave={handleSavePhotoConfig}
        loading={savingPhotoConfig}
      />

      {/* Issue Patta Confirmation Dialog */}
      <Dialog
        open={issueConfirmDialog.open}
        onClose={() =>
          setIssueConfirmDialog({ open: false, certificateId: null })
        }
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            {currentLabels.issueConfirmTitle}
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: "#666" }}>
            {currentLabels.issueConfirmMessage}
          </Typography>
        </Box>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Button
            onClick={() =>
              setIssueConfirmDialog({ open: false, certificateId: null })
            }
            disabled={issuingPatta}
          >
            {currentLabels.cancel}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmIssuePatta}
            disabled={issuingPatta}
            startIcon={
              issuingPatta ? (
                <CircularProgress size={20} />
              ) : (
                <CheckCircleIcon />
              )
            }
          >
            {currentLabels.issueConfirm}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View All Pattas Dialog */}
      <ViewAllPattasDialog
        open={openViewAllPattasDialog}
        onClose={() => setOpenViewAllPattasDialog(false)}
        onPreview={handlePreviewFromViewAll}
      />
    </Container>
  );
};

export default PattaGeneration;
